import { getSmartDateLabel } from '@griply/utils';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { Datepicker } from 'shared/components/ui/datepicker';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './repeat-form-input.style';

export type ScheduleDateFieldProps = {
  value?: Timestamp | null;
  onChange: (date: Date | null) => void;
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateFormat: DateFormatOptions;
  placeholder?: string;
  canClear?: boolean;
  minDate?: Timestamp;
  highlightDates?: Date[];
};

export const ScheduleDateField: React.FC<ScheduleDateFieldProps> = ({
  value,
  onChange,
  today,
  weekStartsOn,
  dateFormat,
  placeholder,
  canClear,
  minDate,
  highlightDates,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const onSelectDate = (date: Date | [Date | null, Date | null] | null) => {
    onChange(Array.isArray(date) ? date[0] : date);
    closeMenu();
  };

  const onClear = () => {
    onChange(null);
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <Styled.Button
        onClick={openMenu}
        type="button"
        $placeholder={!value}
        $hasError={false}
        $noBorders={false}
        $isSmall
      >
        <Styled.Label>
          {value
            ? getSmartDateLabel(value, today, t, { enableWeekdays: true })
            : placeholder ?? t('general.labels.select')}
        </Styled.Label>
        <Styled.ChevronContainer>
          <Icon icon={ChevronLeft} />
        </Styled.ChevronContainer>
      </Styled.Button>

      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          onOutside={closeMenu}
          noBackdrop
          preventReferenceElementOverlap
        >
          <PopupMenuList hasBottomBorder={!!canClear && !!value}>
            <Datepicker
              onChange={onSelectDate}
              selected={value}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              minDate={minDate}
              highlightDates={highlightDates}
              inline
            />
          </PopupMenuList>
          {!!canClear && !!value && (
            <PopupMenuList>
              <PopupMenuListItem>
                <PopupMenuButton onClick={onClear}>
                  {t('general.labels.clear')}
                </PopupMenuButton>
              </PopupMenuListItem>
            </PopupMenuList>
          )}
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
